import "./index.css";
import { useContext, useEffect, useRef, useState } from "react";
import HeaderToolbar from "./HeaderToolbar";
import ChatHistory from "./ChatHistory";
import { Mp3ControllerProvider } from "../../../../Util/providers/Mp3ControllerProvider";
import UserAudioRecorder from "./UserAudioRecorder";
import { AppContext } from "../../../../Util/contexts/AppContext";
import SystemAudioVisualizer from "./SystemAudioVisualizer";
import fetchTranscribeAudio from "../../../../Util/functions/fetchTranscribeAudio";
import fetchLLMSpeakingResponse from "../../../../Util/functions/fetchLLMSpeakingResponse";
import fetchTextToSpeech from "../../../../Util/functions/fetchTextToSpeech";
import Mp3ControllerContext from "../../../../Util/contexts/Mp3ControllerContext";
import { createPortal } from "react-dom";
import SettingsPopup from "./SettingsPopup";
import { AnimatePresence } from "framer-motion";
import AudioDeviceTest from "../Speaking_v1/AudioDeviceTest/AudioDeviceTest";

/*
List of app states:
  0. checkingAudioDevices
  1. waitingForUserAudio
  2. recording
  3. transcribingUserResponse
  4. gettingSystemResponse
  5. GeneratingAudioForSystemResponse
  6. PlayingSystemResponse
*/

const SpeakingSection = ({ examGuid, ExamAttemptId, handleSkipSection, initialMessage }) => {
  const [appState, setAppState] = useState("waitingForUserAudio");
  const {
    userData,
    route,
    displayAuthenticationError,
    primaryColor,
    chatHistory,
    setChatHistory,
  } = useContext(AppContext);
  const [isApplicationPaused, setIsApplicationPaused] = useState(false);
  const [applicationError, setApplicationError] = useState(null);
  const [settings, setSettings] = useState({
    disableAutoSend: false,
    manualAudioStart: false,
    hideTranscriptions: false,
  });
  const [displaySettingsPopup, setDisplaySettingsPopup] = useState(false);
  const [audioDeviceConfirmed, setAudioDeviceConfirmed] = useState(false);
  const [generationCanceled, setGenerationCanceled] = useState(false);
  const [textHidden, setTextHidden] = useState(false);
  const abortRef = useRef(null);

  useEffect(() => {
    setChatHistory([]);
  }, [setChatHistory]);

  const { playLastAudio, isEnded, isPlaying } =
    useContext(Mp3ControllerContext);

  const recoverFromFrozenState = () => {
    // Remove entries from chat history until the end of the array contains a state of completed with isUserMessage false, or until the array is empty
    let newChatHistory = [...chatHistory];
    while (
      newChatHistory.length > 0 &&
      (newChatHistory[newChatHistory.length - 1].state !== "completed" ||
        newChatHistory[newChatHistory.length - 1].isUserMessage)
    ) {
      newChatHistory.pop();
    }
    setChatHistory(newChatHistory);
    setAppState("waitingForUserAudio");
    setApplicationError(null);
    setIsApplicationPaused(true);
  };

  const handleFinishUserRecording = (mp3File) => {
    pushAudioFileToChatHistory(mp3File);
  };

  const pushAudioFileToChatHistory = async (mp3File) => {
    let newChatHistory = [...chatHistory];
    newChatHistory.push({
      id: newChatHistory.length,
      mp3FileUrl: URL.createObjectURL(mp3File),
      mp3File: mp3File,
      message: "",
      isUserMessage: true,
      state: "audioUnprocessed",
    });
    setChatHistory(newChatHistory);
    setAppState("transcribingUserResponse");
  };

  const transcribeUserResponse = async () => {
    let newChatHistory = [...chatHistory];
    const mp3File = newChatHistory[newChatHistory.length - 1].mp3File;
    try {
      const { id, transcription } = await fetchTranscribeAudio(
        mp3File,
        examGuid,
        route,
        userData,
        ExamAttemptId,
        displayAuthenticationError
      );
      newChatHistory[newChatHistory.length - 1].message = transcription;
      newChatHistory[newChatHistory.length - 1].id = id;
      newChatHistory[newChatHistory.length - 1].state = "audioProcessed";

      if (transcription.length === 0) {
        newChatHistory.splice(newChatHistory.length - 1, 1);
        setChatHistory(newChatHistory);
        return setApplicationError({
          message:
            "Recording is empty. Please try recording your response again.",
          buttonText: "Begin Recording",
          buttonAction: () => {
            setAppState("waitingForUserAudio");
            setApplicationError(null);
          },
        });
      }

      setChatHistory(newChatHistory);
      setAppState("gettingSystemResponse");
    } catch (error) {
      return setApplicationError({
        message: "Failed to transcribe audio. Please try again",
        buttonText: "Try again",
        buttonAction: () => {
          transcribeUserResponse();
          setApplicationError(null);
        },
      });
    }
  };

  const getSystemResponse = async () => {
    let newChatHistory = [...chatHistory];
    const transcription = newChatHistory[newChatHistory.length - 1].message;
    newChatHistory.push({
      id: newChatHistory.length,
      mp3FileUrl: "",
      message: "",
      isUserMessage: false,
      state: "generatingResponse",
    });
    setChatHistory(newChatHistory);
    try {
      await fetchLLMSpeakingResponse(
        transcription,
        examGuid,
        route,
        userData,
        ExamAttemptId,
        displayAuthenticationError,
        (token) => {
          newChatHistory[newChatHistory.length - 1].message += token;
          const updatedChatHistory = [...newChatHistory];
          setChatHistory(updatedChatHistory);
        },
        abortRef
      );
      newChatHistory[newChatHistory.length - 1].state = "generatingAudio";
      setChatHistory(newChatHistory);
      setAppState("GeneratingAudioForSystemResponse");
    } catch (error) {
      if (error.message === "Canceled generation") {
        console.log("Generation was canceled by user action");
        setGenerationCanceled(true);
        newChatHistory[newChatHistory.length - 1].state = "generatingAudio";
        setChatHistory(newChatHistory);
        setAppState("GeneratingAudioForSystemResponse");
      } else {
        newChatHistory.splice(newChatHistory.length - 1, 1);
        setChatHistory(newChatHistory);
        return setApplicationError({
          message: "Failed to get system response. Please try again",
          buttonText: "Try again",
          buttonAction: () => {
            getSystemResponse();
            setApplicationError(null);
          },
        });
      }
    }
  };

  const generateAudioForSystemResponse = async () => {
    let newChatHistory = [...chatHistory];
    try {
      const audioBlob = await fetchTextToSpeech(
        newChatHistory[newChatHistory.length - 1].message,
        route,
        userData,
        examGuid,
        ExamAttemptId,
        displayAuthenticationError
      );
      newChatHistory[newChatHistory.length - 1].mp3FileUrl =
        URL.createObjectURL(audioBlob);
      newChatHistory[newChatHistory.length - 1].state = "completed";
      setChatHistory(newChatHistory);
      await new Promise((resolve) => setTimeout(resolve, 100));
      if (generationCanceled) {
        setGenerationCanceled(false);
        setAppState("waitingForUserAudio");
      } else {
        setAppState("PlayingSystemResponse");
        playLastAudio();
      }
    } catch (error) {
      return setApplicationError({
        message:
          "Failed to generate audio for system response. Please try again",
        buttonText: "Try again",
        buttonAction: () => {
          generateAudioForSystemResponse();
          setApplicationError(null);
        },
      });
    }
  };

  const stopTextGeneration = async () => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
      await new Promise((res) => setTimeout(() => res(), 100));
      setAppState("waitingForUserAudio");
      setApplicationError(null);
    }
  };

  const hideText = async () => {
    setTextHidden(!textHidden)
  };

  useEffect(() => {
    if (appState === "transcribingUserResponse") {
      transcribeUserResponse();
    } else if (appState === "gettingSystemResponse") {
      getSystemResponse();
    } else if (appState === "GeneratingAudioForSystemResponse") {
      generateAudioForSystemResponse();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appState]);

  useEffect(() => {
    if (isEnded && appState === "PlayingSystemResponse") {
      setAppState("waitingForUserAudio");
    }
  }, [isEnded, appState]);

  useEffect(() => {
    if (appState === "waitingForUserAudio" && isPlaying) {
      setIsApplicationPaused(true);
    }
    if (!isPlaying && appState === "PlayingSystemResponse") {
      setAppState("waitingForUserAudio");
    }
  }, [isPlaying, appState]);

  return (
    <div className="speakingSectionContainer">
      <HeaderToolbar
        settingsClick={() => setDisplaySettingsPopup(true)}
        pauseClick={() => setIsApplicationPaused(!isApplicationPaused)}
        isApplicationPaused={isApplicationPaused}
        appState={appState}
        stopTextGeneration={stopTextGeneration}
        hideClick={hideText}
        textHidden={textHidden}
      />
      {appState === "waitingForUserAudio" || appState === "recording" ? (
        <UserAudioRecorder
          onRecordingFinish={handleFinishUserRecording}
          recordingStarted={() => setAppState("recording")}
          isApplicationPaused={isApplicationPaused}
          manualAudioStart={settings.manualAudioStart}
          disableAutoSend={settings.disableAutoSend}
          audioDeviceConfirmed={audioDeviceConfirmed}
        />
      ) : appState === "paused" ? (
        <div>Paused</div>
      ) : (
        <SystemAudioVisualizer
          appState={appState}
          applicationError={applicationError}
        />
      )}
      <ChatHistory
        chatHistory={chatHistory}
        appState={appState}
        isApplicationPaused={isApplicationPaused}
        applicationError={applicationError}
        showTranscriptions={!settings.hideTranscriptions}
        ExamAttemptId={ExamAttemptId}
        textHidden={textHidden}
        initialMessage={initialMessage}
      />
      <AnimatePresence mode="wait">
        {!audioDeviceConfirmed && (
          <AudioDeviceTest
            primaryColor={primaryColor}
            continue={() => setAudioDeviceConfirmed(true)}
            cancel={handleSkipSection}
            animationsDisabled={userData.animationsDisabled}
          />
        )}
      </AnimatePresence>
      {displaySettingsPopup &&
        createPortal(
          <SettingsPopup
            settings={settings}
            setSettings={setSettings}
            recoverFromFrozenState={recoverFromFrozenState}
            closePopup={() => setDisplaySettingsPopup(false)}
          />,
          document.body
        )}
    </div>
  );
};

const speakingSectionWrapper = ({
  examGuid,
  ExamAttemptId,
  handleSkipSection,
  initialMessage
}) => {
  return (
    <Mp3ControllerProvider>
      <SpeakingSection
        examGuid={examGuid}
        ExamAttemptId={ExamAttemptId}
        handleSkipSection={handleSkipSection}
        initialMessage={initialMessage}
      />
    </Mp3ControllerProvider>
  );
};

export default speakingSectionWrapper;
